import React, { useState } from 'react';
import {
  Paper,
  Tabs,
  Tab,
  Box,
  Typography,
} from '@mui/material';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import type { TimeSeriesData } from '../services/metricsService';

interface MetricsChartProps {
  data: TimeSeriesData[];
}

const MetricsChart: React.FC<MetricsChartProps> = ({ data }) => {
  const [activeTab, setActiveTab] = useState(0);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const formatNumber = (value: number) => {
    return value.toLocaleString();
  };

  const formatPercentage = (value: number) => {
    return `${(value * 100).toFixed(2)}%`;
  };

  const formatPosition = (value: number) => {
    return value.toFixed(1);
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Performance Trends
      </Typography>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={handleTabChange}>
          <Tab label="Clicks & Impressions" />
          <Tab label="CTR" />
          <Tab label="Position" />
          <Tab label="Combined View" />
        </Tabs>
      </Box>

      {/* Clicks & Impressions Chart */}
      {activeTab === 0 && (
        <ResponsiveContainer width="100%" height={400}>
          <AreaChart data={data}>
            <defs>
              <linearGradient id="colorClicks" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#1976d2" stopOpacity={0.8} />
                <stop offset="95%" stopColor="#1976d2" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="colorImpressions" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#2e7d32" stopOpacity={0.8} />
                <stop offset="95%" stopColor="#2e7d32" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="date"
              tickFormatter={formatDate}
              tick={{ fontSize: 12 }}
            />
            <YAxis tick={{ fontSize: 12 }} tickFormatter={formatNumber} />
            <Tooltip
              labelFormatter={formatDate}
              formatter={(value: number, name: string) => [
                formatNumber(value),
                name === 'clicks' ? 'Clicks' : 'Impressions',
              ]}
            />
            <Legend />
            <Area
              type="monotone"
              dataKey="clicks"
              stroke="#1976d2"
              fillOpacity={1}
              fill="url(#colorClicks)"
              name="Clicks"
            />
            <Area
              type="monotone"
              dataKey="impressions"
              stroke="#2e7d32"
              fillOpacity={1}
              fill="url(#colorImpressions)"
              name="Impressions"
            />
          </AreaChart>
        </ResponsiveContainer>
      )}

      {/* CTR Chart */}
      {activeTab === 1 && (
        <ResponsiveContainer width="100%" height={400}>
          <LineChart data={data}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="date"
              tickFormatter={formatDate}
              tick={{ fontSize: 12 }}
            />
            <YAxis
              tick={{ fontSize: 12 }}
              tickFormatter={(value) => `${(value * 100).toFixed(1)}%`}
            />
            <Tooltip
              labelFormatter={formatDate}
              formatter={(value: number) => [formatPercentage(value), 'CTR']}
            />
            <Legend />
            <Line
              type="monotone"
              dataKey="ctr"
              stroke="#ed6c02"
              strokeWidth={2}
              dot={{ r: 3 }}
              name="CTR"
            />
          </LineChart>
        </ResponsiveContainer>
      )}

      {/* Position Chart */}
      {activeTab === 2 && (
        <ResponsiveContainer width="100%" height={400}>
          <LineChart data={data}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="date"
              tickFormatter={formatDate}
              tick={{ fontSize: 12 }}
            />
            <YAxis
              reversed
              tick={{ fontSize: 12 }}
              domain={[0, 'dataMax + 5']}
            />
            <Tooltip
              labelFormatter={formatDate}
              formatter={(value: number) => [formatPosition(value), 'Position']}
            />
            <Legend />
            <Line
              type="monotone"
              dataKey="position"
              stroke="#9c27b0"
              strokeWidth={2}
              dot={{ r: 3 }}
              name="Position"
            />
          </LineChart>
        </ResponsiveContainer>
      )}

      {/* Combined View */}
      {activeTab === 3 && (
        <Box>
          <ResponsiveContainer width="100%" height={250}>
            <AreaChart data={data}>
              <defs>
                <linearGradient id="colorClicks2" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#1976d2" stopOpacity={0.6} />
                  <stop offset="95%" stopColor="#1976d2" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="date"
                tickFormatter={formatDate}
                tick={{ fontSize: 12 }}
              />
              <YAxis tick={{ fontSize: 12 }} tickFormatter={formatNumber} />
              <Tooltip
                labelFormatter={formatDate}
                formatter={(value: number) => [formatNumber(value), 'Clicks']}
              />
              <Area
                type="monotone"
                dataKey="clicks"
                stroke="#1976d2"
                fillOpacity={1}
                fill="url(#colorClicks2)"
                name="Clicks"
              />
            </AreaChart>
          </ResponsiveContainer>

          <ResponsiveContainer width="100%" height={150}>
            <LineChart data={data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="date"
                tickFormatter={formatDate}
                tick={{ fontSize: 12 }}
              />
              <YAxis
                tick={{ fontSize: 12 }}
                tickFormatter={(value) => `${(value * 100).toFixed(0)}%`}
              />
              <Tooltip
                labelFormatter={formatDate}
                formatter={(value: number) => [formatPercentage(value), 'CTR']}
              />
              <Line
                type="monotone"
                dataKey="ctr"
                stroke="#ed6c02"
                strokeWidth={2}
                dot={false}
                name="CTR"
              />
            </LineChart>
          </ResponsiveContainer>
        </Box>
      )}
    </Paper>
  );
};

export default MetricsChart;
